import React, { useMemo, useState } from 'react';
import { organisationsAPI } from '../../../organisations/api/organisationsAPI';
import StudentsTable from '../students/StudentsTable';
import SelectionBar from '../students/SelectionBar';
import DeleteGroupButton from '../students/DeleteGroupButton';
import EmptyState from '../common/EmptyState';
import { DEFAULT_SORT, nextSort, sortStudents } from '../../lib/studentTable';
import { studentsCsv, csvFilename } from '../../lib/studentsCsv';
import { changeMembers, groupErrorMessage } from '../../lib/groupActions';
import { downloadCsv } from '../../lib/downloadCsv';

const countText = (n) => `${n} ${n === 1 ? 'student' : 'students'}`;

/** What the list is, for the caption: a custom group, a form or a class. */
const listKind = (entry) => {
  if (entry.kind === 'group') return 'Custom group';
  if (entry.kind === 'form') return 'Form';
  if (entry.kind === 'class') return `${entry.subject} class`;
  return null;
};

/**
 * Students tab: completed work per student for the selected scope, with
 * group management. `selection` is lib/scopes' findScope result for the
 * scope on screen. Only custom groups can be changed here; forms and
 * classes come from the school's records and are shown as they are. The
 * parent remounts this tab per scope, so ticks never carry over to another
 * list.
 */
const StudentsTab = ({
  students, loading, error, selection, periodLabel,
  onOpenStudent, onMembershipChanged, onGroupDeleted,
}) => {
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const sorted = useMemo(() => sortStudents(students, sort), [students, sort]);

  const { section, entry } = selection;
  const group = entry.kind === 'group' ? entry : null;
  const isClass = entry.kind === 'form' || entry.kind === 'class';
  const targets = section.groups.filter((g) => g.id !== group?.id);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const toggleAll = (on) => setSelected(on ? new Set(students.map((s) => s.id)) : new Set());

  const runMemberChange = async (change, groupId, successText) => {
    setBusy(true);
    setMessage(null);
    const ids = [...selected];
    const failure = await changeMembers(change, groupId, ids);
    setBusy(false);
    setMessage(failure ? { tone: 'error', text: failure } : { tone: 'notice', text: successText(ids.length) });
    if (!failure) setSelected(new Set());
    onMembershipChanged();
  };

  const addTo = (target) => runMemberChange(
    (g, u) => organisationsAPI.addGroupMember(g, u),
    target.id,
    (n) => `Added ${n} ${n === 1 ? 'student' : 'students'} to ${target.label}.`,
  );

  const removeFromGroup = () => runMemberChange(
    (g, u) => organisationsAPI.removeGroupMember(g, u),
    group.id,
    (n) => `Removed ${n} ${n === 1 ? 'student' : 'students'} from ${group.label}.`,
  );

  const deleteGroup = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await organisationsAPI.deleteGroup(group.id);
      onGroupDeleted(section.entries[0].scope);
    } catch (err) {
      setBusy(false);
      setMessage({ tone: 'error', text: groupErrorMessage(err, 'Could not delete this group. Please try again.') });
    }
  };

  const kind = listKind(entry);
  const caption = [kind, countText(students.length), group ? 'any staff member can change it' : null]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="td-students">
      <div className="td-students__head">
        <div>
          <h2 className="td-students__title">{entry.label}</h2>
          <span className="td-students__caption">{caption}{periodLabel ? ` · ${periodLabel}` : ''}</span>
        </div>
        <div className="td-students__controls">
          {group ? <DeleteGroupButton groupName={group.label} onDelete={deleteGroup} busy={busy} /> : null}
          <button
            type="button"
            className="td-btn"
            disabled={!sorted.length}
            onClick={() => downloadCsv(csvFilename(entry.label), studentsCsv(sorted))}
          >
            Export .csv
          </button>
        </div>
      </div>

      {message ? (
        <div className={message.tone === 'error' ? 'td-error' : 'td-notice'} role="status">{message.text}</div>
      ) : null}
      {error ? <div className="td-error">{error}</div> : null}

      {selected.size > 0 ? (
        <SelectionBar
          count={selected.size}
          targets={targets}
          onAdd={addTo}
          inGroup={Boolean(group)}
          onRemove={removeFromGroup}
          onClear={() => setSelected(new Set())}
          busy={busy}
        />
      ) : null}

      {loading ? <p className="td-loading">Loading…</p> : null}

      {!loading && !error && sorted.length === 0 ? (
        <EmptyState
          title={group ? 'No students in this group yet' : isClass ? 'No students in this class' : 'No students here yet'}
          body={group
            ? `Tick students in ${section.entries[0].label} and add them to this group.`
            : isClass
              ? 'Its students have left or have not joined PathBuddy yet.'
              : 'Students appear here once they join with your school’s link.'}
        />
      ) : null}

      {!loading && sorted.length > 0 ? (
        <>
          <StudentsTable
            students={sorted}
            sort={sort}
            onSort={(key) => setSort((prev) => nextSort(prev, key))}
            selected={selected}
            onToggle={toggle}
            onToggleAll={toggleAll}
            onOpenStudent={onOpenStudent}
          />
          <p className="td-card__note">
            Counts are completed pieces of work in the selected period. Click a column to sort,
            tick students to add them to a group, click a name for their profile.
          </p>
        </>
      ) : null}
    </div>
  );
};

export default StudentsTab;
